import { ReactNode } from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Paper from '@mui/material/Paper';
import Chip from '@mui/material/Chip';
import Divider from '@mui/material/Divider';
import LocalHospitalOutlined from '@mui/icons-material/LocalHospitalOutlined';
import PinDropOutlined from '@mui/icons-material/PinDropOutlined';
import BadgeOutlined from '@mui/icons-material/BadgeOutlined';
import { theme } from '@omnigenbiodata/ui';
import DetailRow from '../DetailRow';

interface ParticipantSiteProps {
  odsCode: string | null;
  siteName: string | null;
  isInitiated: boolean | null;
  siteSystem: string | null;
  siteAddress1: string | null;
  siteAddress2: string | null;
  siteTown: string | null;
  sitePostcode: string | null;
}

interface SiteSectionProps {
  title: string;
  icon: ReactNode;
  children: ReactNode;
}

function getCardTone(isInitiated: boolean | null) {
  if (isInitiated === true) {
    return {
      bgcolor: 'success.lighter',
      borderColor: 'success.light',
      iconColor: 'success' as const,
    };
  }

  if (isInitiated === false) {
    return {
      bgcolor: 'warning.lighter',
      borderColor: 'warning.light',
      iconColor: 'warning' as const,
    };
  }

  return {
    bgcolor: theme.palette.info.lighter,
    borderColor: 'info.light',
    iconColor: 'info' as const,
  };
}

function renderSiteStatus(isInitiated: boolean | null) {
  if (isInitiated === null) {
    return <Chip label="UNKNOWN" color="default" size="small" sx={{ fontWeight: 700 }} />;
  }

  return (
    <Chip
      label={isInitiated ? 'INITIATED' : 'UNINITIATED'}
      color={isInitiated ? 'success' : 'warning'}
      size="small"
      sx={{ fontWeight: 700 }}
    />
  );
}

function SiteSection({ title, icon, children }: SiteSectionProps) {
  return (
    <Box mt={2}>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          mb: 1,
        }}
      >
        {icon}

        <Typography variant="subtitle1" component="h3" sx={{ fontWeight: 700 }}>
          {title}
        </Typography>
      </Box>

      {children}
    </Box>
  );
}

function ParticipantSite({
  odsCode,
  siteName,
  isInitiated,
  siteSystem,
  siteAddress1,
  siteAddress2,
  siteTown,
  sitePostcode,
}: ParticipantSiteProps) {
  const tone = getCardTone(isInitiated);
  const addressLines = [siteAddress1, siteAddress2, siteTown, sitePostcode].filter((line): line is string =>
    Boolean(line),
  );

  return (
    <Box mb={2}>
      <Paper
        elevation={4}
        sx={{
          bgcolor: tone.bgcolor,
          border: 1,
          borderColor: tone.borderColor,
          overflow: 'hidden',
        }}
      >
        <Box p={2}>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              minWidth: 0,
              gap: 1,
              mb: 1,
            }}
          >
            <Box
              sx={{
                width: 36,
                height: 36,
                borderRadius: '50%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                bgcolor: 'background.paper',
                flexShrink: 0,
              }}
            >
              <LocalHospitalOutlined color={tone.iconColor} />
            </Box>

            <Box sx={{ minWidth: 0 }}>
              <Typography variant="h6" component="h2" sx={{ fontWeight: 700, m: 0 }}>
                Enrolment Site
              </Typography>

              <Typography variant="body2" component="p" color="text.secondary">
                Site initiation status, system and address details
              </Typography>
            </Box>
          </Box>

          <DetailRow label="Site Status" value={renderSiteStatus(isInitiated)} />

          <Divider sx={{ my: 2 }} />

          <SiteSection title="Site Identifiers" icon={<BadgeOutlined color={tone.iconColor} fontSize="small" />}>
            <DetailRow label="ODS Code" value={odsCode} />
            <DetailRow label="Site Name" value={siteName} />
            <DetailRow label="Site System" value={siteSystem} />
          </SiteSection>

          <Divider sx={{ my: 2 }} />

          <SiteSection title="Site Address" icon={<PinDropOutlined color={tone.iconColor} fontSize="small" />}>
            {addressLines.length > 0 ? (
              <Box component="address" sx={{ fontStyle: 'normal' }}>
                {addressLines.map((line) => (
                  <Typography key={line} variant="body1" component="p" align="left" mb={0.5}>
                    {line}
                  </Typography>
                ))}
              </Box>
            ) : (
              <Typography variant="body1" component="p" color="text.secondary">
                Not recorded
              </Typography>
            )}
          </SiteSection>
        </Box>
      </Paper>
    </Box>
  );
}

export default ParticipantSite;
